import { ArrowUpRight, CalendarRange, Award, MessageSquareText } from "lucide-react";
import { DEALER } from "@/data/dealer";
import { useTitle } from "@/lib/site";
import { PageHeader } from "@/components/site/Shared";
import { Reveal } from "@/components/motion/Reveal";
import { PromoGraphics, RatingBlock } from "@/components/site/Rating";

const INFO = [
  {
    icon: CalendarRange,
    title: "Based on the last 12 months",
    text: "AutoTrader ratings reflect a seller's performance over the last 12 months, so the score shows their current quality of service.",
  },
  {
    icon: Award,
    title: "Highly Rated awards",
    text: `AutoTrader gives Highly Rated awards for consistently high review ratings over a year. YK Motors has been recognised in ${DEALER.awards.join(", ")}.`,
  },
  {
    icon: MessageSquareText,
    title: "Read them first-hand",
    text: "Every review is written by a customer and published on AutoTrader. We haven't reproduced or edited any review text on this site.",
  },
];

export default function Reviews() {
  useTitle("Customer Reviews | YK Motors");
  return (
    <>
      <PageHeader
        crumbs={[{ label: "Home", to: "/" }, { label: "Reviews" }]}
        eyebrow="Customer reviews"
        title={["Rated 5.0 by", "our customers."]}
        intro={`${DEALER.rating.reviews} customers have reviewed YK Motors on AutoTrader. Read what they say in their own words.`}
      />
      <section className="on-dark bg-charcoal py-16 text-white lg:py-24" aria-label="Rating summary" data-testid="reviews-rating-section">
        <div className="container-x grid gap-12 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <RatingBlock dark />
            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <a href={DEALER.autotrader.reviews} target="_blank" rel="noopener noreferrer" className="btn btn-primary" data-testid="reviews-autotrader-link">
                Read reviews on AutoTrader <ArrowUpRight aria-hidden="true" />
              </a>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={0.1}>
            <PromoGraphics />
            <p className="mt-4 text-sm text-zinc-400">Promotional graphics supplied by YK Motors.</p>
          </Reveal>
        </div>
      </section>
      <section className="py-16 lg:py-24" aria-labelledby="ratings-work">
        <div className="container-x">
          <Reveal className="mb-10 max-w-2xl">
            <p className="eyebrow">About the rating</p>
            <h2 id="ratings-work" className="mt-4 font-display text-3xl font-extrabold leading-[1.04] tracking-tight sm:text-4xl">
              How AutoTrader ratings work.
            </h2>
          </Reveal>
          <div className="grid gap-4 md:grid-cols-3">
            {INFO.map(({ icon: Icon, title, text }, i) => (
              <Reveal key={title} delay={i * 0.08} className="h-full">
                <div className="flex h-full flex-col rounded-lg border border-line p-7">
                  <Icon className="h-7 w-7 text-brand" aria-hidden="true" />
                  <h3 className="mt-8 font-display text-xl font-extrabold">{title}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-steel">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12 flex flex-col items-start justify-between gap-6 rounded-lg bg-paper p-8 md:flex-row md:items-center">
            <div>
              <h3 className="font-display text-2xl font-extrabold">Bought a car from us?</h3>
              <p className="mt-2 text-steel">We'd really appreciate a review on our AutoTrader profile.</p>
            </div>
            <a href={DEALER.autotrader.profile} target="_blank" rel="noopener noreferrer" className="btn btn-dark" data-testid="reviews-leave-review-link">
              Go to our AutoTrader profile <ArrowUpRight aria-hidden="true" />
            </a>
          </Reveal>
        </div>
      </section>
    </>
  );
}
